import React, { useEffect, useRef, useState } from "react";
import { Star } from "lucide-react";
import { cn } from "@/lib/utils";

interface StarRatingProps {
  average: number;
  count: number;
  myRating: number | null;
  onRate: (rating: number) => void;
  disabled?: boolean;
  loading?: boolean;
  canRate?: boolean;
}

const StarRating: React.FC<StarRatingProps> = ({
  average,
  count,
  myRating,
  onRate,
  disabled = false,
  loading = false,
  canRate = false,
}) => {
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState<number | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  const roundedAvg = Math.round(average * 10) / 10;

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setHovered(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const handleRate = (value: number) => {
    onRate(value);
    setOpen(false);
    setHovered(null);
  };

  const displayRating = hovered ?? myRating ?? 0;

  if (!canRate && count === 0) return null;

  return (
    <div ref={ref} className="inline-flex items-center gap-1.5">
      {/* Trigger / summary */}
      <button
        type="button"
        disabled={disabled || loading || !canRate}
        onClick={() => canRate && setOpen((prev) => !prev)}
        className={cn(
          "inline-flex items-center gap-1.5 rounded-full p-1 font-medium text-xs transition-colors",
          canRate && !disabled && !loading
            ? "cursor-pointer text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-100"
            : "cursor-default text-gray-400 dark:text-gray-500"
        )}
        aria-label={myRating ? `Reytinqiniz: ${myRating}/10` : "Reytinq"}
      >
        <Star className={cn("h-3.5 w-3.5 shrink-0", myRating ? "fill-amber-400 text-amber-400" : "")} />
        {count > 0 ? (
          <span className="whitespace-nowrap">
            {roundedAvg}/10 ({count})
          </span>
        ) : (
          "Reytinq"
        )}
      </button>

      {loading && (
        <span className="w-3 h-3 border border-gray-400 border-t-transparent rounded-full animate-spin" />
      )}

      {/* Star picker — only shown when open */}
      {open && (
        <div
          className="inline-flex items-center gap-0.5"
          onMouseLeave={() => setHovered(null)}
        >
          {Array.from({ length: 10 }, (_, i) => {
            const starValue = i + 1;
            const isActive = starValue <= displayRating;
            const isMyRating = myRating !== null && starValue <= myRating && !hovered;

            return (
              <button
                key={i}
                type="button"
                onClick={() => handleRate(starValue)}
                onMouseEnter={() => setHovered(starValue)}
                className={cn(
                  "transition-transform select-none cursor-pointer hover:scale-110",
                  isActive
                    ? isMyRating
                      ? "text-blue-500 dark:text-indigo-300"
                      : "text-amber-400"
                    : "text-gray-300 dark:text-gray-600"
                )}
                aria-label={`${starValue} ulduz`}
              >
                <Star className={cn("h-4 w-4", isActive ? "fill-current" : "")} />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default StarRating;
